import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/DataTable';
import { Button, Modal, FormField, ListPageHeader } from '../components/ui';
import { CardHero, CardStatusBadge, CardSplitButton } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const emptyPeriod = {
  code: '', name: '', year: new Date().getFullYear(),
  start_date: '', end_date: '', notes: '', is_active: true, status: 'open',
};

function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('tr-TR');
}

export default function FiscalPeriodsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ ...emptyPeriod });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/fiscal-periods');
      if (r.data.success) setList(r.data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openAdd = () => {
    const yr = new Date().getFullYear();
    setForm({
      ...emptyPeriod,
      year: yr,
      code: String(yr),
      name: `${yr} Yılı`,
      start_date: `${yr}-01-01`,
      end_date: `${yr}-12-31`,
    });
    setSelected(null);
    setMsg(null);
    setModalMode('add');
  };

  const openView = (p) => {
    setForm({
      code: p.code,
      name: p.name,
      year: p.year,
      start_date: p.start_date ? p.start_date.substring(0, 10) : '',
      end_date: p.end_date ? p.end_date.substring(0, 10) : '',
      notes: p.notes || '',
      is_active: p.is_active !== false,
      status: p.status || 'open',
    });
    setSelected(p);
    setMsg(null);
    setModalMode('view');
  };

  const closeModal = () => { setModalMode(null); setSelected(null); setMsg(null); };

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    if (!form.code || !form.name || !form.start_date || !form.end_date)
      return setMsg({ type: 'error', text: 'Kod, ad, başlangıç ve bitiş tarihi zorunlu' });
    setSaving(true); setMsg(null);
    try {
      const body = { ...form, year: parseInt(form.year) };
      if (modalMode === 'edit') await api.put('/fiscal-periods/' + selected.id, body);
      else await api.post('/fiscal-periods', body);
      await fetchList();
      if (mode === 'close') closeModal();
      else if (mode === 'new') { setSelected(null); setForm({ code: '', name: '', year: new Date().getFullYear(), start_date: '', end_date: '', status: 'open' }); setModalMode('add'); setMsg(null); }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm('Bu dönemi silmek istiyor musunuz?')) return;
    try {
      await api.delete('/fiscal-periods/' + selected.id);
      closeModal();
      fetchList();
    } catch (err) {
      alert(err.response?.data?.message || 'Silinemedi');
    }
  };

  const tf = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const ro = modalMode === 'view';
  const isNew = modalMode === 'add';

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const inEdit = modalMode === 'edit' || modalMode === 'add';
  const inView = modalMode === 'view';

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Çalışma Dönemleri"
        count={list.length}
        unit="dönem"
        onRefresh={fetchList}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni Dönem</Button>}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="fiscal-periods-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Henüz dönem yok"
          data={list}
          rowKey="id"
          onRowClick={p => openView(p)}
          columns={[
            { id: 'code', header: 'Kod', accessor: 'code', sortable: true, width: 100,
              cell: p => <span className="stock-code">{p.code}</span> },
            { id: 'name', header: 'Ad', accessor: 'name', sortable: true,
              cell: p => <span className="stock-name-cell">{p.name}</span> },
            { id: 'year', header: 'Yıl', accessor: 'year', sortable: true, width: 80 },
            { id: 'start_date', header: 'Başlangıç', accessor: 'start_date', sortable: true, width: 120,
              cell: p => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.start_date)}</span> },
            { id: 'end_date', header: 'Bitiş', accessor: 'end_date', sortable: true, width: 120,
              cell: p => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(p.end_date)}</span> },
            { id: 'status', header: 'Durum', accessor: p => p.status === 'closed' ? 'Kapalı' : 'Açık', sortable: true, width: 100,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: p => <span className={'stock-badge ' + (p.status === 'closed' ? 'passive' : 'active')} style={{ background: p.status === 'closed' ? 'rgba(220,38,38,0.1)' : 'rgba(16,185,129,0.1)', color: p.status === 'closed' ? '#dc2626' : '#16a34a' }}>{p.status === 'closed' ? 'Kapalı' : 'Açık'}</span> },
            { id: 'is_active', header: 'Aktif', accessor: p => p.is_active ? 'Aktif' : 'Pasif', sortable: true, width: 90,
              headerStyle: { textAlign: 'center' }, cellStyle: { textAlign: 'center' },
              cell: p => <span className={'stock-badge ' + (p.is_active ? 'active' : 'passive')}>{p.is_active ? 'Aktif' : 'Pasif'}</span> },
          ]}
        />
      </div>

      {modalMode !== null && (
        <Modal
          icon={null}
          title={isNew ? 'Yeni Dönem' : 'Çalışma Dönemi'}
          size="md"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEdit && <Button variant="ghost" onClick={modalMode === 'edit' ? () => setModalMode('view') : closeModal}>{isNew ? 'Vazgeç' : 'İptal'}</Button>}
                {inEdit && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && <Button onClick={() => { setMsg(null); setModalMode('edit'); }}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {!isNew && (
            <CardHero
              icon="📅"
              title={form.name || 'Dönem'}
              subtitle={(form.start_date ? new Date(form.start_date).toLocaleDateString('tr-TR') : '?') + ' — ' + (form.end_date ? new Date(form.end_date).toLocaleDateString('tr-TR') : '?')}
              statusBadge={
                form.status === 'closed'
                  ? <CardStatusBadge label="Kapalı" color="#dc2626" bg="rgba(220,38,38,0.15)" />
                  : <CardStatusBadge label="Açık" preset="active" />
              }
            />
          )}
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <FormField label="Kod" required={!ro}>
              <input disabled={ro} value={form.code} onChange={e => tf('code', e.target.value)} placeholder={ro ? '' : 'örn. 2026'} maxLength={20} />
            </FormField>
            <FormField label="Yıl" required={!ro}>
              <input type={ro ? 'text' : 'number'} disabled={ro} value={form.year} onChange={e => tf('year', e.target.value)} min={2000} max={2100} />
            </FormField>
            <FormField label="Ad" required={!ro} fullWidth>
              <input disabled={ro} value={form.name} onChange={e => tf('name', e.target.value)} placeholder={ro ? '' : 'örn. 2026 Yılı'} />
            </FormField>
            <FormField label="Başlangıç" required={!ro}>
              <input type={ro ? 'text' : 'date'} disabled={ro} value={ro ? fmtDate(form.start_date) : form.start_date} onChange={e => tf('start_date', e.target.value)} />
            </FormField>
            <FormField label="Bitiş" required={!ro}>
              <input type={ro ? 'text' : 'date'} disabled={ro} value={ro ? fmtDate(form.end_date) : form.end_date} onChange={e => tf('end_date', e.target.value)} />
            </FormField>
            <FormField label="Durum">
              <select disabled={ro} value={form.status} onChange={e => tf('status', e.target.value)}>
                <option value="open">Açık</option>
                <option value="closed">Kapalı</option>
              </select>
            </FormField>
            <FormField label="Aktif">
              <select disabled={ro} value={form.is_active ? '1' : '0'} onChange={e => tf('is_active', e.target.value === '1')}>
                <option value="1">Aktif</option>
                <option value="0">Pasif</option>
              </select>
            </FormField>
            <FormField label="Notlar" fullWidth>
              <textarea rows={2} disabled={ro} value={form.notes || ''} onChange={e => tf('notes', e.target.value)} />
            </FormField>
          </div>
        </Modal>
      )}
    </div>
  );
}
